import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { Location } from '../types';

class LocationService {
  private collectionName = 'locations';

  async create(locationData: Omit<Location, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.collectionName), {
      ...locationData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  }

  async getAll(): Promise<Location[]> {
    const snapshot = await getDocs(collection(db, this.collectionName));
    const locations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Location));
    // 클라이언트 측 정렬 (zone, name 순)
    return locations.sort((a, b) => {
      const zoneCompare = a.zone.localeCompare(b.zone);
      if (zoneCompare !== 0) return zoneCompare;
      return a.name.localeCompare(b.name);
    });
  }

  async getById(id: string): Promise<Location | null> {
    const docRef = doc(db, this.collectionName, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Location;
    }
    return null;
  }

  async getByZone(zone: string): Promise<Location[]> {
    const q = query(
      collection(db, this.collectionName),
      where('zone', '==', zone)
    );
    const snapshot = await getDocs(q);
    const locations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Location));
    // 클라이언트 측 정렬
    return locations.sort((a, b) => a.name.localeCompare(b.name));
  }

  async update(id: string, locationData: Partial<Location>): Promise<void> {
    const docRef = doc(db, this.collectionName, id);
    await updateDoc(docRef, {
      ...locationData,
      updatedAt: Timestamp.now()
    });
  }

  async delete(id: string): Promise<void> {
    const docRef = doc(db, this.collectionName, id);
    await deleteDoc(docRef);
  }

  async checkCapacity(locationId: string): Promise<boolean> {
    const location = await this.getById(locationId);
    if (!location) return false;
    return location.currentCount < location.capacity;
  }

  async incrementCount(locationId: string): Promise<void> {
    const location = await this.getById(locationId);
    if (!location) return;
    await this.update(locationId, {
      currentCount: location.currentCount + 1
    });
  }

  async decrementCount(locationId: string): Promise<void> {
    const location = await this.getById(locationId);
    if (!location || location.currentCount === 0) return;
    await this.update(locationId, {
      currentCount: location.currentCount - 1
    });
  }
}

export const locationService = new LocationService();
